import type { ReactNode } from "react";
import { EllipsisIcon, PlusIcon } from "lucide-react";

import { cn } from "~/lib/utils";

export interface PlanningBoardColumnHeaderProps {
  readonly label: string;
  readonly count: number;
  /** Status / priority glyph shown before the label. */
  readonly leading?: ReactNode;
  readonly onCreate?: (() => void) | undefined;
  readonly createLabel?: string | undefined;
  /** Optional overflow menu trigger (column options). */
  readonly onMenu?: (() => void) | undefined;
  readonly menuLabel?: string | undefined;
  readonly className?: string;
}

/**
 * Linear-like Kanban column chrome: leading glyph, label, muted count, trailing
 * optional ⋯ / + controls on a quiet rounded header strip.
 */
export function PlanningBoardColumnHeader(props: PlanningBoardColumnHeaderProps) {
  return (
    <div className={cn("flex items-center gap-1.5 px-2.5 py-2 text-sm", props.className)}>
      {props.leading != null ? (
        <span className="inline-flex shrink-0 items-center" aria-hidden="true">
          {props.leading}
        </span>
      ) : null}
      <span className="min-w-0 truncate font-medium text-foreground">{props.label}</span>
      <span className="shrink-0 tabular-nums text-muted-foreground">{props.count}</span>
      <div className="ml-auto flex shrink-0 items-center gap-0.5">
        {props.onMenu != null ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              props.onMenu?.();
            }}
            aria-label={props.menuLabel ?? `${props.label} column options`}
            className="inline-flex size-touch items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:size-7"
          >
            <EllipsisIcon className="size-3.5" aria-hidden="true" />
          </button>
        ) : null}
        {props.onCreate != null ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              props.onCreate?.();
            }}
            aria-label={props.createLabel ?? `Add in ${props.label}`}
            className="inline-flex size-touch items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:size-7"
          >
            <PlusIcon className="size-3.5" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
